// Shared contract for generating Economic Process Modelling (EPM) decisions with AI.
// The client builds the evidence from the model; the server asks the AI for decisions grounded in it.
// Shared with linq-server (copied by its `npm run copy`), so it must only import from this folder.

export const EPM_LEVERS = ['eliminate', 'automate', 'substitute', 'amplify', 'resequence', 'protect', 'leverage', 'investigate'] as const;
export type EpmLever = typeof EPM_LEVERS[number];

export const EPM_NODE_KINDS = ['action', 'role', 'system', 'info'] as const;
export type EpmNodeKind = typeof EPM_NODE_KINDS[number];

export const EPM_LEVELS = ['Low', 'Medium', 'High'] as const;
export type EpmLevel = typeof EPM_LEVELS[number];

// a signal found in the model that a decision can address. The id is "<kind>:<node key>"
export interface EpmSignalEvidence {
    id: string;
    description: string;
}

export interface EpmRoleEvidence {
    name: string;
    people: number | null;          // null when people aren't set, so capacity is unknown
    demandHours: number;
    capacityHours: number | null;
    loadPercent: number | null;
    cost: number;
    actions: number;
    revenueContributed?: number;    // its share of the revenue its work creates: these add up across roles
    valueContributed?: number;
    revenueAtRisk?: number;         // the revenue lost if it stopped: the work only it does has no other route
}

export interface EpmSystemEvidence {
    name: string;
    actions: number;
    models: number;
    cost: number;
    revenueContributed?: number;    // its share of the revenue the work it's used on creates: these add up across systems
    valueContributed?: number;
    revenueAtRisk?: number;         // the revenue lost if it stopped: the work only it is used on has no other route
}

export interface EpmActionEvidence {
    name: string;
    model: string;
    performedBy: 'manual' | 'automated' | 'assisted' | 'external' | 'unassigned';
    roles: string[];
    systems: string[];
    itemsPerYear: number;
    hours: number;
    cost: number;
    revenueFlowing: number;         // revenue allocated back to the action from the outcomes it leads to: all it supports
    value: number;
    revenueContributed?: number;    // its share of that revenue, by its work: these add up across actions
    valueContributed?: number;
    revenueAtRisk?: number;         // the revenue lost if it stopped, as its items have no other route
    workKind?: string;              // value adding, necessary, exception, not value adding or waiting (see WORK_TYPE)
    waitHoursEach?: number;         // where items wait: how long each item waits there, with no hours of work
    waitHours?: number;             // and the hours of waiting a year, over all its items
    next?: EpmNextStep[];           // where its items go next, so the flow can be restructured
}

// a step an action's items go to next, and the information they go as
export interface EpmNextStep {
    step: string;
    via: string;
}

export interface EpmOutputEvidence {
    name: string;
    model: string;
    value: number;                  // value a year: value per item x items, from the values users set
    revenue: number;
    userValue: number | null;       // value per item the user set on the output, or null when none is set
    derivedValue: number;           // value per item LINQ derived from how the output is used, 0 to 100 across the organisation
    held?: string;                  // how it is held, from its type: system, file, paper, verbal, or unsaid
    itemsPerYear: number;
    downstreamUses: number;
    modelsUsedIn: number;           // other models the output is used in
    outcomesReached: number;
    hoursToProduce: number;
    costToProduce: number;
    state: string;
    asset?: string;                 // the information asset the output is a state of, if any
}

// an information asset: the information itself, which outputs and the information passed between steps are states of.
// Its figures are read across all its states
export interface EpmAssetEvidence {
    name: string;
    description: string;
    states: number;                 // in the models scored
    models: number;                 // models scored that it has states in
    itemsPerYear: number;
    value: number;                  // value a year, from the values users set
    revenue: number;
    derivedValue: number | null;    // 0 to 100 across the organisation's assets, null until LINQ has worked it out
    uses: number;                   // actions and outcomes using it, not counting the work moving it between its states
    modelsUsedIn: number;           // other models using it
    outcomesReached: number;
    externalOrganisations: number;
    hoursOnStates: number;          // a year, on producing all its states
    costOfStates: number;
    statesHeldOutside: number;      // states kept in local tools, files, on paper or by word of mouth
    state: string;
    owner: string | null;           // the role accountable for it, from the register
    classification: string | null;  // public, internal, confidential or personal
    systemOfRecord: string | null;  // the system holding its authoritative copy
    risks: string[];                // what puts it at risk, from its register and how its states are held
}

// everything the AI may use. It must not invent nodes or figures beyond this
export interface EpmDecisionEvidence {
    scope: string;
    workspace: string;
    currency: string;
    totals: {
        revenue: number;
        relativeValue: number;
        cost: number;
        peopleCost: number;
        systemCost: number;
        netOperatingValue: number;
        frictionCost: number;           // dead ends, exception work, re-keying and manual bridges together, each step once
        frictionHours: number;
        deadEndCost: number;
        exceptionCost: number;
        exceptionHours: number;
        notValueAddingCost?: number;    // work said not to add value (waste other than exception work)
        notValueAddingHours?: number;
        rekeyCost: number;              // typing paper or spoken information into a system
        rekeyHours: number;
        bridgeCost: number;             // carrying information between systems by hand, beyond what is counted above
        bridgeHours: number;
        peopleHours: number;
        workHoursPerItem?: number;      // the time each item coming in takes: the work, and the waiting where nobody works on it
        waitHoursPerItem?: number;
        flowEfficiencyPercent?: number; // the share of an item's time that is work
    };
    roles: EpmRoleEvidence[];
    systems: EpmSystemEvidence[];
    actions: EpmActionEvidence[];
    outputs: EpmOutputEvidence[];
    assets?: EpmAssetEvidence[];    // when the organisation has information assets
    detours: EpmDetourEvidence[];
    signals: EpmSignalEvidence[];
    dataGaps: string[];
}

// a detour from the main flow: items leave it at one point and return at another, and the work between is exception
// work (unless the user has said it is normal work on the actions)
export interface EpmDetourEvidence {
    leavesAt: string;
    returnsAt: string;
    model: string;
    steps: string[];                // the actions on the detour that count as exception work
    shareOfItems: number;           // percent of the items leaving the split that take the detour
    itemsPerYear: number;
    hours: number;
    cost: number;
    confirmed: boolean;             // the user marked the work as exception work, rather than LINQ reading it
}

// a decision as the AI returns it
export interface AIEpmDecision {
    title: string;
    lever: EpmLever;
    gate: boolean;                  // a guardrail that must run before the decisions that depend on it
    signal: string;
    rationale: string;
    addresses: string[];            // ids of the signals the decision addresses
    nodes: { kind: EpmNodeKind; name: string }[];
    impacts: { dimension: string; movement: string }[];
    impact: number;                 // 1 (small) to 10 (large)
    effort: EpmLevel;
    confidence: EpmLevel;
    grounded: boolean;              // true when the decision rests on modelled figures rather than inference
    dependsOn: string;              // the title of a decision it depends on, or "None"
}

export interface AIEpmDecisionsResponse {
    decisions: AIEpmDecision[];
}

// the evidence is bounded so a request can't grow without limit
export const EPM_EVIDENCE_LIMITS = { actions: 30, outputs: 30, assets: 20, roles: 30, systems: 30, detours: 15, signals: 20 };

// ---------- value suggestions ----------
// AI suggests a value per item (0 to 100, on the same scale as LINQ's derived value) for the outputs in the evidence,
// with its reasoning, for the user to accept or not. It must not invent outputs or figures beyond the evidence

export interface AIEpmValueSuggestion {
    name: string;                   // the output, by name as given
    model: string;                  // the model it is in, as given
    value: number;                  // suggested value per item, 0 to 100
    rationale: string;              // one or two sentences on what the value rests on
}

export interface AIEpmValueSuggestionsResponse {
    suggestions: AIEpmValueSuggestion[];
}

export const EPM_VALUE_LIMITS = { suggestions: 30, value: 100 };

// why a suggestion can't be used, or undefined when it can: it must name an output in the evidence and a value 0 to 100
export const checkEpmValueSuggestion = (suggestion: AIEpmValueSuggestion, evidence: EpmDecisionEvidence): string | undefined => {
    const output = (evidence.outputs || []).find((o) => sameName(o.name, suggestion.name) && (!suggestion.model || sameName(o.model, suggestion.model)));
    if (!output) return `the output ${suggestion.name} isn't in the model`;
    if (typeof suggestion.value !== 'number' || !Number.isFinite(suggestion.value) || suggestion.value < 0 || suggestion.value > EPM_VALUE_LIMITS.value) return 'it has no valid value';
    return undefined;
};

// ---------- future state ----------
// The user picks decisions and AI turns them into changes to the model, which LINQ applies to a new project so the
// future state can be scored against current state.

// the changes AI can make:
//   eliminate: the action no longer needs doing, so it takes no time (it stays in the flow so the flow still joins up)
//   automate: a system performs the action instead of its roles
//   assist: a system helps the action's roles, who keep it
//   reassign: another role performs the action
//   retime: the action takes a different time
//   staff: the role has a different number of people
//   share: a different share of items takes a connection: from information, or a step through the information it
//     creates, to a step (such as the first step of a detour). The other connections from the same point share the rest
//   volume: a step that sets how often it happens (an input, say) does a different number of items a year
//   remove: the action is taken out of the flow: what it worked on goes straight to the steps after it
//   add: a new action, creating new information, is put in the flow after a step (or information), done by a role or
//     system (which may be new)
export const EPM_CHANGE_KINDS = ['eliminate', 'automate', 'assist', 'reassign', 'retime', 'staff', 'share', 'volume', 'remove', 'add'] as const;
export type EpmChangeKind = typeof EPM_CHANGE_KINDS[number];

// a change as AI returns it. Structured output has no optional fields, so what a kind doesn't use is null
export interface AIEpmChange {
    decision: string;               // the title of the decision it carries out
    kind: EpmChangeKind;
    action: string | null;          // the action changed, by name (every kind but staff)
    model: string | null;           // the model the action is in
    role: string | null;            // reassign: the role giving the action up (null for all of them). staff: the role
    newRole: string | null;         // reassign: the role taking the action on, which may be new
    system: string | null;          // automate and assist: the system, which may be new
    percent: number | null;         // retime and assist: the action's new time as a percentage of what it takes today. share: the share of items, 0 to 100
    people: number | null;          // staff: how many people the role has. reassign: for a new role
    from: string | null;            // share: the step or information the items leave, by name. add: the step or information the new step follows
    items: number | null;           // volume: items a year
    output: string | null;          // add: the information the new step creates
    minutes: number | null;         // add: the new step's time per item
    description: string;            // the change in a sentence
}

// a decision as sent for its changes
export interface EpmFutureStateDecision {
    title: string;
    lever: EpmLever;
    signal: string;
    rationale: string;
    nodes: { kind: EpmNodeKind; name: string }[];
}

export interface EpmFutureStateRequest {
    evidence: EpmDecisionEvidence;
    decisions: EpmFutureStateDecision[];
}

export interface AIEpmFutureStateResponse {
    changes: AIEpmChange[];
}

export const EPM_FUTURE_STATE_LIMITS = { decisions: 10, changes: 40, people: 1000, percent: 200, items: 100_000_000, minutes: 100_000 };

const sameName = (a: string | null | undefined, b: string | null | undefined) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// why a change can't be used, or undefined when it can. Actions and the roles giving them up must be in the evidence
// (so in the model); a system, or a role taking an action on, may be new
export const checkEpmChange = (change: AIEpmChange, evidence: EpmDecisionEvidence): string | undefined => {
    if (!EPM_CHANGE_KINDS.includes(change.kind)) return 'it is not a change LINQ can make';
    if (change.kind === 'staff') {
        if (!(evidence.roles || []).some((r) => sameName(r.name, change.role))) return `the role ${change.role} isn't in the model`;
        if (change.people === null || !Number.isInteger(change.people) || change.people < 0 || change.people > EPM_FUTURE_STATE_LIMITS.people) return 'it has no valid number of people';
        return undefined;
    }
    // a new step: in a model the evidence has, after a step or information found when it's made, done by someone
    if (change.kind === 'add') {
        if (!change.action?.trim()) return 'it names no new step';
        if (!(evidence.actions || []).some((a) => sameName(a.model, change.model))) return `the model ${change.model} isn't in the evidence`;
        if (!change.from?.trim()) return 'it names no step for the new one to follow';
        if (!change.newRole?.trim() && !change.system?.trim()) return 'it names no role or system to do the new step';
        if (change.minutes !== null && change.minutes !== undefined && !(Number.isFinite(change.minutes) && change.minutes >= 0 && change.minutes <= EPM_FUTURE_STATE_LIMITS.minutes)) return 'it has no valid time';
        return undefined;
    }
    if (!(evidence.actions || []).some((a) => sameName(a.name, change.action) && (!change.model || sameName(a.model, change.model)))) return `the action ${change.action} isn't in the model`;
    if (change.kind === 'share') {
        if (!change.from?.trim()) return 'it names nowhere the items leave from';
        if (change.percent === null || !Number.isFinite(change.percent) || change.percent < 0 || change.percent > 100) return 'it has no valid share of items';
        return undefined;
    }
    if (change.kind === 'volume') {
        return change.items !== null && Number.isFinite(change.items) && change.items >= 0 && change.items <= EPM_FUTURE_STATE_LIMITS.items ? undefined : 'it has no valid number of items';
    }
    if ((change.kind === 'automate' || change.kind === 'assist') && !change.system?.trim()) return 'it names no system';
    if (change.kind === 'reassign') {
        if (!change.newRole?.trim()) return 'it names no role to take the action on';
        if (change.role && !(evidence.roles || []).some((r) => sameName(r.name, change.role))) return `the role ${change.role} isn't in the model`;
    }
    const needsPercent = change.kind === 'retime';
    if ((needsPercent || change.percent !== null) && (change.percent === null || change.percent < 0 || change.percent > EPM_FUTURE_STATE_LIMITS.percent)) return 'it has no valid time';
    return undefined;
};
